/*
  Copyright 2018-2020 National Geographic Society

  Use of this software does not constitute endorsement by National Geographic
  Society (NGS). The NGS name and NGS logo may not be used for any purpose without
  written permission from NGS.

  Licensed under the Apache License, Version 2.0 (the "License"); you may not use
  this file except in compliance with the License. You may obtain a copy of the
  License at

      https://www.apache.org/licenses/LICENSE-2.0

  Unless required by applicable law or agreed to in writing, software distributed
  under the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR
  CONDITIONS OF ANY KIND, either express or implied. See the License for the
  specific language governing permissions and limitations under the License.
*/

import React from 'react';

import { isSuperAdmin } from '@marapp/earth-shared';

import { useAuth0 } from '../../auth/auth0';
import { MAP_ADMIN_URL } from '../../config';
import AsyncPage from './async';
import AuthenticatedPage from './authenticated';
import AuthorizedPage from './authorized';

const Main = ({ router, isWebGL }) => {
  const { isLoading, isAuthenticated, isEmailVerified, userData } = useAuth0();
  const { pathname, type, routesMap } = router;
  const { page, isAuthRequired, isAuthzRequired, fallbackRouteResolver } = routesMap[type];
  const context = { pathname, isLoading, isEmailVerified, isAuthenticated };

  if (userData && userData.allGroups?.length === 0 && isSuperAdmin(userData.roles as any)) {
    window.location.assign(MAP_ADMIN_URL);
  }

  let PageComponent;
  switch (true) {
    case isAuthRequired && isAuthRequired(context) && isAuthzRequired(context):
      PageComponent = AuthorizedPage;
      break;
    case (isAuthRequired && isAuthRequired(context)) || (isAuthenticated && !isEmailVerified):
      PageComponent = AuthenticatedPage;
      break;
    default:
      PageComponent = AsyncPage;
  }

  switch (page) {
    case 'home':
      return (
        <PageComponent
          page="home"
          fallbackRoute={fallbackRouteResolver(context)}
          isLoading={isLoading}
          isWebGL={isWebGL}
        />
      );
    case 'earth':
      return <PageComponent page="earth" fallbackRoute={fallbackRouteResolver(context)} />;
    case 'elsa':
      return <PageComponent page="elsa" fallbackRoute={fallbackRouteResolver(context)} />;
    case 'experience':
      return <PageComponent page="experience" fallbackRoute={fallbackRouteResolver(context)} />;
    case 'change-email':
      return <PageComponent page="change-email" fallbackRoute={fallbackRouteResolver(context)} />;
    case 'profile':
      return <PageComponent page="profile" fallbackRoute={fallbackRouteResolver(context)} />;
    case 'not-found':
      return <PageComponent page="not-found" fallbackRoute={fallbackRouteResolver(context)} />;
    case 'error':
      return <PageComponent page="error" fallbackRoute={fallbackRouteResolver(context)} />;
    case 'unauthorized':
      return <PageComponent page="unauthorized" fallbackRoute={fallbackRouteResolver(context)} />;
    case 'verify-email':
      return <PageComponent page="verify-email" />;
  }
};

export default Main;
